import { useCallback, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { FeatureGate } from "@/components/app/FeatureGate"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { API_BASE } from "@/config/api"
import { formatDate } from "@/lib/format"

const API = API_BASE
const POLL_MS = 5000

export default function QueueDisplay() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const [queues, setQueues] = useState([])
  const [queueId, setQueueId] = useState(params.get("queue") || "")
  const [tickets, setTickets] = useState([])
  const [error, setError] = useState(false)

  const loadQueues = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/queues`)
      const list = Array.isArray(data) ? data : data.queues || []
      setQueues(list)
      setError(false)
      return list
    } catch {
      setError(true)
      return []
    }
  }, [])

  const loadTickets = useCallback(async (id, signal) => {
    if (!id) {
      setTickets([])
      return
    }
    try {
      const { data } = await axios.get(`${API}/queues/${id}/tickets`, { signal })
      const list = Array.isArray(data) ? data : data.tickets || []
      setTickets(list)
      setError(false)
    } catch (err) {
      if (axios.isCancel?.(err) || err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return
      setError(true)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const list = await loadQueues()
      if (cancelled) return
      if (!queueId && list.length) {
        const first = list[0].id
        setQueueId(first)
        const next = new URLSearchParams(params)
        next.set("queue", first)
        setParams(next, { replace: true })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!queueId) return
    const controller = new AbortController()
    loadTickets(queueId, controller.signal)
    const timer = setInterval(() => {
      loadTickets(queueId, controller.signal)
    }, POLL_MS)
    return () => {
      controller.abort()
      clearInterval(timer)
    }
  }, [queueId, loadTickets])

  const onQueueChange = (id) => {
    setQueueId(id)
    const next = new URLSearchParams(params)
    next.set("queue", id)
    setParams(next, { replace: true })
  }

  const called = tickets.filter((t) => t.status === "called")
  const waiting = tickets.filter((t) => t.status === "waiting")
  const selected = queues.find((q) => q.id === queueId)

  return (
    <FeatureGate
      feature="queues"
      title={t("app.queues.gateTitle")}
      description={t("app.queues.gateDescription")}
    >
      <div className="min-h-screen bg-background text-foreground p-6 md:p-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">
              {t("app.queues.display.kicker")}
            </p>
            <h1 className="font-display text-3xl md:text-5xl font-medium mt-1">
              {selected?.name || t("app.queues.display.title")}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {queues.length > 1 && (
              <Select value={queueId} onValueChange={onQueueChange}>
                <SelectTrigger className="w-[200px] rounded-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {queues.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Link
              to="/app/queues"
              className="text-xs text-muted-foreground underline underline-offset-2"
            >
              {t("app.queues.display.backToStaff")}
            </Link>
          </div>
        </div>

        {error && (
          <p className="text-sm text-destructive mb-4">{t("app.queues.toast.loadFailed")}</p>
        )}

        <section className="rounded-3xl border border-border bg-card p-8 md:p-12 mb-8">
          <p className="text-sm md:text-base text-muted-foreground mb-4">
            {t("app.queues.display.nowCalled")}
          </p>
          {called.length ? (
            <ul className="space-y-4">
              {called.map((ticket) => (
                <li key={ticket.id} className="font-display text-4xl md:text-6xl font-medium tracking-tight">
                  {ticket.client_name || t("app.queues.walkIn")}
                  {ticket.priority > 0 && (
                    <span className="text-lg md:text-2xl text-muted-foreground ml-3">
                      {t("app.queues.priorityLabel", { n: ticket.priority })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-display text-3xl md:text-5xl text-muted-foreground">
              {t("app.queues.display.noneCalled")}
            </p>
          )}
        </section>

        <section>
          <p className="text-sm md:text-base text-muted-foreground mb-4">
            {t("app.queues.display.waiting", { count: waiting.length })}
          </p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {waiting.map((ticket, idx) => (
              <li
                key={ticket.id}
                className="rounded-2xl border border-border bg-card px-5 py-4 flex items-baseline justify-between gap-3"
              >
                <div>
                  <p className="text-xs text-muted-foreground">#{idx + 1}</p>
                  <p className="font-medium text-lg">{ticket.client_name || t("app.queues.walkIn")}</p>
                  {ticket.appointment_start && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(ticket.appointment_start)}
                    </p>
                  )}
                </div>
                {ticket.priority > 0 && (
                  <span className="text-xs rounded-full border px-2 py-0.5">
                    P{ticket.priority}
                  </span>
                )}
              </li>
            ))}
            {!waiting.length && (
              <li className="text-muted-foreground text-sm col-span-full">
                {t("app.queues.display.noneWaiting")}
              </li>
            )}
          </ul>
        </section>
      </div>
    </FeatureGate>
  )
}
